import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { DateFilterFn, MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { DayjsDateAdapter } from './dayjs-date-adapter.service';
import { MatInputModule } from '@angular/material/input';
import dayjs from 'dayjs';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

export const PICKER_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD',
    monthYearA11yLabel: 'YYYY',
  },
};

export type rangeType = 'startDate' | 'endDate';

@Component({
  selector: 'work-ui-input-datepicker',
  standalone: true,
  providers: [
    {
      provide: DateAdapter,
      useClass: DayjsDateAdapter,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'de-CH',
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: PICKER_DATE_FORMATS,
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputDatepickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputDatepickerComponent),
      multi: true,
    },
  ],
  imports: [MatFormFieldModule, MatDatepickerModule, TranslateModule, FormsModule, ReactiveFormsModule, MatInputModule],
  templateUrl: './input-datepicker.component.html',
  styleUrl: './input-datepicker.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class InputDatepickerComponent implements OnInit, ControlValueAccessor, OnChanges, Validator {
  @ViewChild('startDateInput') startDateInput: ElementRef | undefined;
  @ViewChild('endDateInput') endDateInput: ElementRef | undefined;
  @ViewChild('singleDateInput') singleDateInput: ElementRef | undefined;
  // general
  @Input() variant: 'dateSingle' | 'dateRange' = 'dateSingle';
  @Input() label = '';
  @Input() minDate: Date | string | null = null;
  @Input() maxDate: Date | string | null = null;
  @Input() disabled = false;
  @Input() disableHint = false;
  @Input() required = false;
  @Input() maxWidth: number | string | undefined;
  @Input() readonly = false;
  @Input() readonlyNoBorders = false;
  // single date
  @Input() singleDate: string | null | undefined = '';
  @Input() singleDatePlaceholder = 'DD.MM.YYYY';
  @Input() singleDateFormControlName = '';
  @Output() singleDateChange = new EventEmitter<string | null>();
  public singleDateFormControl = new FormControl(dayjs().format('DD.MM.YYYY'));
  // date range
  @Input() rangeStartDate: string | null = '';
  @Input() rangeEndDate: string | null = '';
  @Input() rangeStartDatePlaceholder = '';
  @Input() rangeEndDatePlaceholder = '';
  @Input() rangeStartDateFormControlName = 'startDate';
  @Input() rangeEndDateFormControlName = 'endDate';
  @Output() rangeStartDateChange = new EventEmitter<string | null>();
  @Output() rangeEndDateChange = new EventEmitter<string | null>();
  public rangeDateForm: FormGroup<{ [key: string]: FormControl }> = new FormGroup({});
  private controlContainer = inject(ControlContainer, { optional: true });
  private inputDebounceTime = 500;
  private singleDateInputSubject: Subject<string> = new Subject<any>();
  private rangeDateInputSubject: Subject<{ value: string; type: rangeType }> = new Subject<any>();

  ngOnInit() {
    if (this.variant === 'dateSingle') {
      this.singleDateInputSubject.pipe(debounceTime(this.inputDebounceTime), distinctUntilChanged()).subscribe((value: string) => {
        this.processInput(value);
      });
      if (this.singleDate) {
        this.writeValue(this.singleDate);
        this.singleDateFormControl.setValue(this.singleDate);
      }
      this.setSingleDateOptionsFromFormControl();
    } else if (this.variant === 'dateRange') {
      this.rangeDateInputSubject.pipe(debounceTime(this.inputDebounceTime), distinctUntilChanged()).subscribe((result) => {
        this.handleRangeDateChange(result.value, result.type);
      });
      const parentFormGroup = this.controlContainer?.control as FormGroup;
      if (parentFormGroup) {
        this.rangeDateForm = parentFormGroup;
      } else {
        this.rangeDateForm.addControl(this.rangeStartDateFormControlName, new FormControl(this.rangeStartDate));
        this.rangeDateForm.addControl(this.rangeEndDateFormControlName, new FormControl(this.rangeEndDate));
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rangeStartDate']?.currentValue) {
      this.setRangeControlValue(this.rangeStartDateFormControlName, this.rangeStartDate);
    }

    if (changes['rangeEndDate']?.currentValue) {
      this.setRangeControlValue(this.rangeEndDateFormControlName, this.rangeEndDate);
    }

    if (changes['disabled']) {
      this.applyDisabledState();
    }
  }

  /**
   * Apply the disabled state to the underlying FormControls so the inner input(s) and
   * the matDatepicker reflect it. The @Input on its own only sets a flag — disabling
   * an input bound via [formControl] must happen on the FormControl itself.
   */
  private applyDisabledState(): void {
    const apply = (control: FormControl) => {
      if (this.disabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    };
    apply(this.singleDateFormControl);
    Object.values(this.rangeDateForm.controls).forEach((control) => apply(control as FormControl));
  }

  public onRangeInput(event: Event, rangeType: rangeType) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const formControl =
      this.rangeDateForm.controls[rangeType === 'startDate' ? this.rangeStartDateFormControlName : this.rangeEndDateFormControlName];
    formControl.setErrors(null);
    this.rangeDateInputSubject.next({ value, type: rangeType });
  }

  public handleRangeDateChange(value: any, rangeType: rangeType): void {
    let controlName = this.rangeStartDateFormControlName;
    let changeEmitter = this.rangeStartDateChange;
    if (rangeType === 'endDate') {
      controlName = this.rangeEndDateFormControlName;
      changeEmitter = this.rangeEndDateChange;
    }
    const formControl = this.rangeDateForm.controls[controlName];
    formControl.setErrors(null);

    // value from datepicker
    if (dayjs.isDayjs(value)) {
      value = dayjs(value).format('YYYY-MM-DD');
      formControl.setValue(value);
      changeEmitter.emit(value);
      return;
    }

    // value from input
    const isValidInput = this.isValidInputDateString(value);
    if (isValidInput) {
      value = this.getParsedAndFormattedDate(value);
      formControl.setValue(value);
      changeEmitter.emit(value);
    } else {
      formControl.setErrors({ invalidDate: true });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.applyDisabledState();
  }

  writeValue(value: any): void {
    if (value) {
      this.singleDate = dayjs(value).format('YYYY-MM-DD');
    }
  }

  validate(): ValidationErrors | null {
    return this.validateInput();
  }

  onDateSingleChange(event: MatDatepickerInputEvent<Date> | string): void {
    const eventValue = typeof event === 'string' ? event : event.value;
    const effectiveDate = eventValue === '' ? null : dayjs(eventValue).format('YYYY-MM-DD');
    if (effectiveDate !== 'Invalid Date') {
      this.singleDateFormControl.setValue(effectiveDate);
      this.onChange(effectiveDate);
      this.singleDateChange.emit(effectiveDate);
    }
    this.onTouched();
    this.validateInput();
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.singleDateFormControl.setErrors(null);
    this.singleDateInputSubject.next(value);
  }

  protected dateFilter: DateFilterFn<Date | null> = (d: Date | null) => {
    if (!d) {
      return true;
    }
    const minDate = this.minDate ? dayjs(this.minDate) : undefined;
    const maxDate = this.maxDate ? dayjs(this.maxDate) : undefined;
    if (minDate && maxDate) {
      return d >= minDate.toDate() && d <= maxDate.toDate();
    } else if (minDate) {
      return d >= minDate.toDate();
    } else if (maxDate) {
      return d <= maxDate.toDate();
    } else {
      return true;
    }
  };

  private setRangeControlValue(controlName: string, value: any): void {
    const control = this.rangeDateForm.controls[controlName];
    if (control) {
      control.setValue(value);
    }
  }

  private setSingleDateOptionsFromFormControl() {
    if (this.variant === 'dateSingle' && this.controlContainer && this.singleDateFormControlName) {
      this.singleDateFormControl = this.controlContainer.control?.get(this.singleDateFormControlName) as FormControl;
      if (this.singleDateFormControl) {
        if (this.singleDateFormControl.validator) {
          const validator = this.singleDateFormControl.validator({} as AbstractControl);
          this.required = validator && validator['required'];
        }
      }
    }
  }

  // check if field has full date formats DD.MM.YYYY, DD/MM/YYYY, D.M.YYYY, D/M/YYYY, D.MM.YYYY, DD.M.YYYY, etc.
  private isValidInputDateString(date: string): boolean {
    const datePattern = /^(0?[1-9]|[12][0-9]|3[01])[./](0?[1-9]|1[012])[./]\d{4}$/;
    return datePattern.test(date);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: any) => void = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  private processInput(value: string): void {
    if (this.isValidInputDateString(value)) {
      const formattedDate = this.getParsedAndFormattedDate(value);
      this.singleDateFormControl.setValue(formattedDate);
    } else if (value.length >= 8) {
      this.validateInput();
    }
  }

  private getParsedAndFormattedDate(value: string): string {
    const parsedDate = dayjs(value, 'D.M.YYYY');
    return parsedDate.format('YYYY-MM-DD');
  }

  private validateInput() {
    const inputFieldValue = this.singleDateInput?.nativeElement.value;
    if (this.singleDateFormControl.value === null && inputFieldValue !== '') {
      this.singleDateFormControl.setErrors({ invalidDate: true });
      return { invalidDate: true };
    } else {
      this.singleDateFormControl.setErrors(null);

      // If the value passes all validations
      return null;
    }
  }
}
